/**
 * A line's price before it is saved, worked out the way the server does it
 * (services/orders/pricing.py) so the figure shown is the figure the order
 * will carry.
 *
 * Only a preview: the server prices the saved line itself. Where it would
 * convert the unit by a category's weight — a bag of nails — the browser does
 * not know the weight, so the amount is left for the server rather than
 * guessed.
 */

/** This product's rate at a price tier, or null when it has none. */
export const rateFor = (product, tier) => {
  const row = product?.prices?.find((p) => p.tier === tier)
  return row ? Number(row.rate) : null
}

/**
 * The quantity in the product's pricing unit, or null if not convertible here.
 *
 * Only the base unit converts in the browser. A SKU's own `conversion_rate`
 * was read here too, and no longer is — the server stopped using it, and a
 * preview that still did would quote a carton at a weight the saved line
 * does not use. Every other unit is weighed by its category, which the
 * browser does not hold, so the amount is left to the server.
 */
export const toBase = (product, quantity, unit) => {
  const wanted = String(unit ?? '').toUpperCase()
  if (!wanted || wanted === String(product.base_unit ?? '').toUpperCase()) return quantity
  return null
}

/**
 * { rate, amount } for a line. `rate` is per base unit at the tier; `amount`
 * is what the line will total including GST, or null when only the server
 * can work it out.
 */
export const estimateLine = (product, tier, quantity, unit) => {
  const rate = rateFor(product, tier)
  const amount = Number(quantity)
  if (rate == null || !Number.isFinite(amount) || amount <= 0) return { rate, amount: null }
  const base = toBase(product, amount, unit)
  if (base == null) return { rate, amount: null }
  const gross = rate * base
  // A tax-inclusive rate already contains GST; otherwise it goes on top.
  const total = product.inclusive_of_tax
    ? gross
    : gross * (1 + Number(product.tax_rate ?? 0) / 100)
  return { rate, amount: Math.round(total * 100) / 100 }
}
